import {
	File as FileIcon,
	FileText,
	Image as ImageIcon,
	Upload,
	X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

interface DocumentUploaderProps {
	onFilesChange?: (files: File[]) => void;
	maxFiles?: number;
	maxFileSize?: number; // in bytes
	acceptedFileTypes?: string[];
	className?: string;
}

export function DocumentUploader({
	onFilesChange,
	maxFiles = 10,
	maxFileSize = 20 * 1024 * 1024, // 20MB default
	acceptedFileTypes = [
		"image/*",
		"application/pdf",
		".doc",
		".docx",
		".xls",
		".xlsx",
		".txt",
		".pdf",
	],
	className,
}: DocumentUploaderProps) {
	const [files, setFiles] = useState<File[]>([]);
	const [isDragging, setIsDragging] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFiles = useCallback(
		(newFiles: FileList | null) => {
			if (!newFiles) return;

			const fileArray = Array.from(newFiles);
			const validFiles: File[] = [];

			for (const file of fileArray) {
				// Check file size
				if (file.size > maxFileSize) {
					alert(
						`File "${file.name}" is too large. Maximum size is ${maxFileSize / 1024 / 1024}MB`,
					);
					continue;
				}

				// Check if we've reached max files
				if (files.length + validFiles.length >= maxFiles) {
					alert(`Maximum ${maxFiles} files allowed`);
					break;
				}

				validFiles.push(file);
			}

			if (validFiles.length > 0) {
				const updatedFiles = [...files, ...validFiles];
				setFiles(updatedFiles);
				onFilesChange?.(updatedFiles);
			}
		},
		[files, maxFiles, maxFileSize, onFilesChange],
	);

	const removeFile = useCallback(
		(index: number) => {
			const updatedFiles = files.filter((_, i) => i !== index);
			setFiles(updatedFiles);
			onFilesChange?.(updatedFiles);
		},
		[files, onFilesChange],
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);
			handleFiles(e.dataTransfer.files);
		},
		[handleFiles],
	);

	const handleFileInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			handleFiles(e.target.files);
			// Reset input value to allow selecting the same file again
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		},
		[handleFiles],
	);

	const getFileIcon = (file: File) => {
		if (file.type.startsWith("image/")) {
			return <ImageIcon className="size-8 text-blue-500" />;
		}
		if (file.type === "application/pdf") {
			return <FileText className="size-8 text-red-500" />;
		}
		return <FileIcon className="size-8 text-gray-500" />;
	};

	const formatFileSize = (bytes: number) => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
	};

	return (
		<div className={cn("w-full space-y-4", className)}>
			{/* Drop Zone */}
			{/** biome-ignore lint/a11y/noStaticElementInteractions: unique use case for uploader */}
			{/** biome-ignore lint/a11y/useKeyWithClickEvents: will address at some point */}
			<div
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				className={cn(
					"border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer",
					isDragging
						? "border-primary bg-primary/5"
						: "border-border hover:border-primary/50",
				)}
				onClick={() => fileInputRef.current?.click()}
			>
				<input
					ref={fileInputRef}
					type="file"
					multiple
					accept={acceptedFileTypes.join(",")}
					onChange={handleFileInputChange}
					className="hidden"
				/>
				<div className="flex flex-col items-center gap-2">
					<Upload className="size-12 text-muted-foreground" />
					<div className="space-y-1">
						<p className="text-sm font-medium">
							<span className="text-primary">Click to upload</span> or drag and
							drop
						</p>
						<p className="text-xs text-muted-foreground">
							Maximum {maxFiles} files, up to {maxFileSize / 1024 / 1024}MB each
						</p>
					</div>
				</div>
			</div>

			{/* File Previews */}
			{files.length > 0 && (
				<div className="space-y-2">
					<p className="text-sm font-medium">
						Selected Files ({files.length}/{maxFiles})
					</p>
					<div className="grid grid-cols-1 gap-2">
						{files.map((file, index) => (
							<Card key={`${file.name}-${index}`} className="relative">
								<CardContent className="p-3">
									<div className="flex items-center gap-3">
										{/* File Icon or Image Preview */}
										<div className="shrink-0">
											{file.type.startsWith("image/") ? (
												<div className="size-16 rounded overflow-hidden bg-muted">
													<img
														src={URL.createObjectURL(file)}
														alt={file.name}
														className="size-full object-cover"
														onLoad={(e) => {
															// Clean up object URL after image loads
															URL.revokeObjectURL(
																(e.target as HTMLImageElement).src,
															);
														}}
													/>
												</div>
											) : (
												<div className="size-16 rounded bg-muted flex items-center justify-center">
													{getFileIcon(file)}
												</div>
											)}
										</div>

										{/* File Info */}
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium truncate">
												{file.name}
											</p>
											<p className="text-xs text-muted-foreground">
												{formatFileSize(file.size)}
											</p>
										</div>

										{/* Remove Button */}
										<Button
											type="button"
											variant="ghost"
											size="icon-sm"
											onClick={(e) => {
												e.stopPropagation();
												removeFile(index);
											}}
											className="shrink-0"
										>
											<X className="size-4" />
										</Button>
									</div>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
